<template>
    <div class="wsc animated fadeIn">
        <!-- Page Header -->
        <div class="wsc-page-header">
            <button class="wsc-back-btn" @click="$router.back()">
                <b-icon icon="arrow-left" class="mr-1"></b-icon>
                {{ $t('back') }}
            </button>
        </div>

        <!-- Form Card -->
        <div>
            <div class="wsc-card" v-if="workspace !== undefined">
                <!-- Card Header -->
                <div class="wsc-card-header">
                    <div class="wsc-card-icon">
                        <b-icon icon="columns-gap" font-scale="1.4"></b-icon>
                    </div>
                    <h2 class="wsc-card-title">{{ $t('newWorkspace') }}</h2>
                    <p class="wsc-card-subtitle">{{ $t('workspaceDescription') }}</p>
                </div>

                <!-- Form Body -->
                <div class="wsc-card-body">
                    <b-form @submit="save">
                        <InputField
                            name="name"
                            :label="$t('name')"
                            v-model="workspace.name"
                            @error="fieldError"
                            :rules="{ required: true, length: { min: 2, max: 50 } }"
                        />

                        <div class="wsc-form-actions">
                            <button type="submit" class="wsc-submit-btn">
                                <b-icon icon="check2" class="mr-2"></b-icon>
                                {{ $t('saveChanges') }}
                            </button>
                        </div>
                    </b-form>
                </div>
            </div>
        </div>

        <!-- Error Dialog -->
        <main-dialog
            :show="errorDialog.show"
            :title="$t('error')"
            @close="errorDialog.show = false"
        >
            <p>{{ errorDialog.message }}</p>
            <template #footer>
                <b-button variant="secondary" @click="errorDialog.show = false">{{ $t('close') }}</b-button>
            </template>
        </main-dialog>
    </div>
</template>

<style scoped lang="scss">
.wsc {
    padding: 0;
}

/* ── Page Header ───────────────────────────────────────────────────── */
.wsc-page-header {
    margin-bottom: 1.75rem;
}

.wsc-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.45rem 0.9rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-secondary);
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        color: var(--text-primary);
        border-color: var(--border-hover);
        background: var(--bg-card-secondary);
    }
}

/* ── Card ──────────────────────────────────────────────────────────── */
.wsc-card {
    width: 100%;
    max-width: 520px;
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
}

.wsc-card-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 2.25rem 2rem 1.5rem;
    background: linear-gradient(135deg, rgba(94, 231, 223, 0.08) 0%, rgba(180, 144, 202, 0.08) 100%);
    border-bottom: 1px solid var(--border-default);
}

.wsc-card-icon {
    width: 60px;
    height: 60px;
    border-radius: 16px;
    background: linear-gradient(135deg, #5ee7df 0%, #b490ca 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
    box-shadow: 0 4px 16px rgba(94, 231, 223, 0.3);

    svg {
        color: #fff;
    }
}

.wsc-card-title {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 0.35rem;
}

.wsc-card-subtitle {
    font-size: 0.85rem;
    color: var(--text-secondary);
    margin: 0;
    max-width: 340px;
}

.wsc-card-body {
    padding: 1.75rem 2rem;
}

/* ── Form Actions ──────────────────────────────────────────────────── */
.wsc-form-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--border-default);
}

.wsc-submit-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.65rem 1.5rem;
    font-size: 0.9rem;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
    color: #fff;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(102, 126, 234, 0.35);
    transition: all 0.25s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
    }

    &:active {
        transform: translateY(0);
    }
}
</style>

<script>
import Input from "@/components/form/Input"

export default {
    name: "Create",
    components: {
        InputField: Input
    },
    data: function () {
        return {
            errors: [],
            errorDialog: { show: false, message: '' },
            workspace: {
                name: ''
            }
        }
    },
    computed: {},

    methods: {
        save(e) {
            e.preventDefault()

            if (this.checkForErrors()) {
                this.errorDialog.message = this.$t('fieldsNotValid')
                this.errorDialog.show = true
                return
            }

            this.$http.post("/v1/project/workspace", this.workspace).then(resp => {
                this.$bvToast.toast(this.$t('workspaceCreate'), {
                    variant: 'success',
                    title: "Success"
                })

                let project = this.$store.getters.currentProject
                project.workspaces.push(resp.data);
                this.$store.commit("UPDATE_PROJECTS", project)

                this.$router.push({ path: '/admin/workspace' })
            }).catch(error => {
                if (error.response.status == 500) {
                    this.$serverErrorModal(error)
                    return
                }

                this.$bvToast.toast(error.response.data.message, {
                    variant: 'danger',
                    title: this.$t('error')
                })
            })
        },

        fieldError(error) {
            let found = this.errors.find((i, e) => {
                return i.field == error.field
            })

            if (found === undefined) {
                this.errors.push(error)
            } else {
                this.errors.map(e => {
                    if (found.field == e.field)
                        e.error = error.error
                })
            }
        },

        checkForErrors() {
            return this.errors.find((i, e) => {
                return i.error
            })
        }
    },
    mounted() {
        if (!this.$isAdmin()) {
            this.$router.back()
        }
    }
}
</script>
